import { csvField, rawText } from './final-report-export';
import { OUTCOME_LABEL } from './outcome-copy';
import { normaliseIssues } from './report-issues';
import { plural, text } from './report-values';

import type { Observation } from './module-report-types';
import type { JobHeader, TenantOutcome, TenantRow } from '../schemas/bulk-migration';

/**
 * Membership programs with a GL code gap, per client: the list CSM uses to ask a
 * museum for its GL codes (INI-0495).
 *
 * The Membership programs migrator reports a gap several ways, and this reads them:
 * - a program it did NOT migrate, with one of its GL `notMigrated` codes (accounting
 *   is on in Salesforce and a GL code is missing, or Veevart could not read whether
 *   accounting is on);
 * - a program it migrates WITHOUT a GL code: the record's `glCode` column is blank,
 *   it is not the guest program, and nothing stops it. After a launch, the run's
 *   `…_GL_CODE_ABSENT` warning says the same;
 * - a non-guest program with a blank GL code that something else holds: a Veevart
 *   2.0 conflict (the dry-run `preview.action` is `conflict: <code>`) or another
 *   `notMigrated` reason. Whether the GL code is then required is known only once
 *   that is fixed, so CSM asks the museum for one anyway. Not when the reason is on
 *   the Veevart 2.0 side (`…_DELETED_IN_V2`, `…_INVALID_V2_ID`): the program is not
 *   migrated by design, so its GL code would never be used.
 *
 * The guest program is exempt from the GL code and the Split GL code, not from the Tax
 * GL code: any quarantine lists it, and in practice the Tax rule's two,
 * `…_TAX_GL_CODE_REQUIRED` or `…_ACCOUNTING_UNVERIFIED` (the tax rate is above 0, the
 * Tax GL code is blank and whether accounting is on could not be read). A record with no
 * `glCode` column at all (an analysis made before the backend wrote it) says nothing
 * about its GL code, so no gap is inferred from it.
 *
 * The backend checks the Tax and Split GL codes only on a program nothing else holds,
 * and the record has no column for them: a conflict or another `notMigrated` reason
 * (the guest's `…_GUEST_CONFLICT` too) hides a blank Tax or Split GL code until it is
 * fixed and the client analysed again.
 *
 * Read only: nothing here suggests the org default GL code was applied, because it
 * never is.
 */

export const MEMBERSHIP_PROGRAMS_MODULE_ID = 'fundraising-membership-programs';

export const GL_CODE_ABSENT = 'FUNDRAISING_MEMBERSHIP_PROGRAMS_GL_CODE_ABSENT';
export const GL_CODE_REQUIRED = 'FUNDRAISING_MEMBERSHIP_PROGRAMS_GL_CODE_REQUIRED';
export const TAX_GL_CODE_REQUIRED = 'FUNDRAISING_MEMBERSHIP_PROGRAMS_TAX_GL_CODE_REQUIRED';
export const SPLIT_GL_CODE_REQUIRED = 'FUNDRAISING_MEMBERSHIP_PROGRAMS_SPLIT_GL_CODE_REQUIRED';
export const ACCOUNTING_UNVERIFIED = 'FUNDRAISING_MEMBERSHIP_PROGRAMS_ACCOUNTING_UNVERIFIED';

/**
 * What a run WARNING is about, as the report's lede says it ("the GL code"). Only
 * `GL_CODE_ABSENT` is a warning; the other GL codes are `notMigrated` reasons, which
 * never reach the lede.
 */
export const GL_WARNING_TOPICS: Readonly<Record<string, string>> = {
    [GL_CODE_ABSENT]: 'the GL code',
};

interface GlFix {
    /** Who makes the fix; "What to do" starts with it. */
    readonly owner: 'museum' | 'veevart';
    readonly fix: string;
}

const OWNER_LABEL: Readonly<Record<GlFix['owner'], string>> = {
    museum: 'Museum',
    veevart: 'Veevart',
};

const GL_FIXES: Readonly<Record<string, GlFix>> = {
    [GL_CODE_REQUIRED]: {
        owner: 'museum',
        fix: 'add a GL code to the program in Salesforce (Auctifera__GL_Code__c), then analyse again.',
    },
    [TAX_GL_CODE_REQUIRED]: {
        owner: 'museum',
        fix: 'add a Tax GL code to the program in Salesforce (Auctifera__Tax_GL_Code__c); its tax rate is above 0.',
    },
    [SPLIT_GL_CODE_REQUIRED]: {
        owner: 'museum',
        fix: 'add a Split Receipting Donation GL code in Salesforce (Auctifera__Split_Receipting_Donation_GL_Code__c); split receipting is on.',
    },
    [ACCOUNTING_UNVERIFIED]: {
        owner: 'veevart',
        fix: 'give the Salesforce integration user read access to Auctifera__Accounting__c / vnfp__Enable_Accounting__c, then analyse again. Not a museum action.',
    },
    [GL_CODE_ABSENT]: {
        owner: 'museum',
        fix: 'add a GL code to the program in Salesforce (Auctifera__GL_Code__c); Veevart analyses the client again before launching. Required when accounting is on.',
    },
};

/** "Museum: …" or "Veevart: …": every "What to do" starts with who acts, so CSM can route it. */
const withOwner = ({ owner, fix }: GlFix): string => `${OWNER_LABEL[owner]}: ${fix}`;

/** The fix for a GL code, starting with who makes it; `null` for any other code. */
export function glWhatToDo(code: string): string | null {
    const fix = GL_FIXES[code];
    return fix === undefined ? null : withOwner(fix);
}

/** The `notMigrated` codes that are a GL gap; any other one is some other quarantine. */
const QUARANTINE_CODES: ReadonlySet<string> = new Set([
    GL_CODE_REQUIRED,
    TAX_GL_CODE_REQUIRED,
    SPLIT_GL_CODE_REQUIRED,
    ACCOUNTING_UNVERIFIED,
]);

/**
 * `notMigrated` codes for a program Veevart 2.0 does not take whatever its GL code (it
 * was deleted there, or its stamp is not a Veevart 2.0 id): no GL code gap.
 */
const NOT_MIGRATED_BY_DESIGN: ReadonlySet<string> = new Set([
    'FUNDRAISING_MEMBERSHIP_PROGRAMS_DELETED_IN_V2',
    'FUNDRAISING_MEMBERSHIP_PROGRAMS_INVALID_V2_ID',
]);

/** The client, as both a dry-run row and a final-report row can say it. */
export type GlGapTenant = Pick<
    TenantRow['tenant'],
    'tenantId' | 'slug' | 'displayName' | 'orgId' | 'isSandbox'
>;

/** A record, from a dry-run row (`RecordRow`) or a final report (`FinalRecord`). */
export interface GlGapRecord {
    readonly sourceId: string;
    readonly title: string;
    readonly columns: Readonly<Record<string, unknown>>;
    readonly notMigrated: { readonly code: string; readonly message: string } | null;
    /** The migrator's dry-run preview; its `action` is `conflict: <code>` on a conflict. */
    readonly preview?: unknown;
    /** Absent on a dry-run row; read only after a launch. */
    readonly tracking?: { readonly warnings: unknown } | null | undefined;
}

/** The smallest client row both a dry-run and the final report can rebuild. */
export interface GlGapRow {
    readonly tenant: GlGapTenant;
    /** The dry-run's outcome; `null` for a final-report row, which has its own category. */
    readonly outcome: TenantOutcome | null;
    readonly records: readonly GlGapRecord[];
    /** How many records the analysis found; `records` holds at most what the runner kept. */
    readonly recordsTotal: number;
}

/**
 * - `quarantine`: not migrated for a GL code (`code` is the GL `notMigrated` code);
 * - `absent`: migrates with no GL code (`code` is `GL_CODE_ABSENT`);
 * - `conflict`: blank GL code, held by a Veevart 2.0 conflict (`code` is the conflict's);
 * - `held`: blank GL code, not migrated for another reason (`code` is that reason),
 *   except a Veevart 2.0-side one (`NOT_MIGRATED_BY_DESIGN`), which is no gap.
 */
export type GlGapKind = 'quarantine' | 'absent' | 'conflict' | 'held';

export interface GlGap {
    readonly tenant: GlGapTenant;
    readonly outcome: TenantOutcome | null;
    readonly sourceId: string;
    readonly title: string;
    readonly guest: boolean;
    /** The program's GL code, trimmed; `null` when it has none. */
    readonly glCode: string | null;
    readonly kind: GlGapKind;
    readonly code: string;
    /** The backend's own sentence, when it gave one. */
    readonly message: string | null;
    /** The client has more programs than the runner kept: the list is not complete. */
    readonly capped: boolean;
}

/**
 * A non-guest program whose `glCode` column is there and blank. A missing column is
 * not a blank one: the analysis predates the backend writing it, so nothing is known.
 */
export function lacksGlCode(columns: Readonly<Record<string, unknown>>): boolean {
    return 'glCode' in columns && text(columns.glCode) === null && columns.guest !== true;
}

/**
 * A program whose blank GL code is a gap ({@link lacksGlCode}), unless Veevart 2.0
 * does not take it (`NOT_MIGRATED_BY_DESIGN`), so its GL code would never be used.
 * The gap list and the final report's "No GL code" chip both read it.
 */
export function hasBlankGlCodeGap(record: Pick<GlGapRecord, 'columns' | 'notMigrated'>): boolean {
    const { notMigrated } = record;
    return (
        lacksGlCode(record.columns) &&
        (notMigrated === null || !NOT_MIGRATED_BY_DESIGN.has(notMigrated.code))
    );
}

const CONFLICT_PREFIX = 'conflict: ';

/** The conflict code of a dry-run preview whose `action` is `conflict: <code>`, else `null`. */
function conflictOf(preview: unknown): string | null {
    if (typeof preview !== 'object' || preview === null || !('action' in preview)) return null;
    const { action } = preview;
    return typeof action === 'string' && action.startsWith(CONFLICT_PREFIX)
        ? action.slice(CONFLICT_PREFIX.length)
        : null;
}

function absentWarning(record: GlGapRecord): { code: string; message: string } | null {
    const warnings = normaliseIssues(record.tracking?.warnings);
    return warnings.find((warning) => warning.code === GL_CODE_ABSENT) ?? null;
}

function gapOf(row: GlGapRow, record: GlGapRecord): GlGap | null {
    const guest = record.columns.guest === true;
    const base = {
        tenant: row.tenant,
        outcome: row.outcome,
        sourceId: record.sourceId,
        title: record.title,
        guest,
        glCode: text(record.columns.glCode),
        capped: row.recordsTotal > row.records.length,
    };
    const quarantine = record.notMigrated;
    if (quarantine !== null && QUARANTINE_CODES.has(quarantine.code)) {
        return { ...base, kind: 'quarantine', code: quarantine.code, message: quarantine.message };
    }
    // The guest program never needs a GL code, whatever an older run warned.
    if (guest) return null;
    const warning = absentWarning(record);
    if (warning !== null) {
        return { ...base, kind: 'absent', code: GL_CODE_ABSENT, message: warning.message };
    }
    if (!hasBlankGlCodeGap(record)) return null;
    if (quarantine !== null) {
        return { ...base, kind: 'held', code: quarantine.code, message: quarantine.message };
    }
    const conflict = conflictOf(record.preview);
    if (conflict !== null) return { ...base, kind: 'conflict', code: conflict, message: null };
    return { ...base, kind: 'absent', code: GL_CODE_ABSENT, message: null };
}

function byTenantThenTitle(
    a: { tenant: GlGapTenant; title: string },
    b: { tenant: GlGapTenant; title: string },
): number {
    return (
        a.tenant.displayName.localeCompare(b.tenant.displayName) || a.title.localeCompare(b.title)
    );
}

/** Every gap, sorted by client name, then program name. */
export function glGapsOf(rows: readonly GlGapRow[]): GlGap[] {
    return rows
        .flatMap((row) => row.records.map((record) => gapOf(row, record)))
        .filter((gap): gap is GlGap => gap !== null)
        .sort(byTenantThenTitle);
}

export interface GlGapsSummary {
    readonly programs: number;
    /** Distinct clients with at least one gap. */
    readonly clients: number;
    /** The GL quarantines and `GL_CODE_ABSENT`, by code. */
    readonly byCode: ReadonlyMap<string, number>;
    /** Blank-GL programs a Veevart 2.0 conflict holds. */
    readonly conflicts: number;
    /** Blank-GL programs not migrated for another reason. */
    readonly held: number;
    /** Clients with a gap whose program list the runner capped. */
    readonly capped: number;
}

export function glGapsSummary(gaps: readonly GlGap[]): GlGapsSummary {
    const byCode = new Map<string, number>();
    for (const gap of gaps) {
        if (gap.kind === 'quarantine' || gap.kind === 'absent') {
            byCode.set(gap.code, (byCode.get(gap.code) ?? 0) + 1);
        }
    }
    return {
        programs: gaps.length,
        clients: new Set(gaps.map((gap) => gap.tenant.tenantId)).size,
        byCode,
        conflicts: gaps.filter((gap) => gap.kind === 'conflict').length,
        held: gaps.filter((gap) => gap.kind === 'held').length,
        capped: new Set(gaps.filter((gap) => gap.capped).map((gap) => gap.tenant.tenantId)).size,
    };
}

const count = (n: number): string => n.toLocaleString('en-US');

/** One sentence per kind of gap present, in the order a reader acts on them. */
function gapSentences(summary: GlGapsSummary): string[] {
    const of = (code: string): number => summary.byCode.get(code) ?? 0;
    const sentences: readonly (readonly [number, (n: number) => string])[] = [
        [
            of(GL_CODE_REQUIRED),
            (n) =>
                `${count(n)} not migrated: accounting is on in Salesforce; the museum adds the GL code and the client is analysed again.`,
        ],
        [
            of(TAX_GL_CODE_REQUIRED),
            (n) => `${count(n)} not migrated: the Tax GL code is missing with a tax rate above 0.`,
        ],
        [
            of(SPLIT_GL_CODE_REQUIRED),
            (n) =>
                `${count(n)} not migrated: the Split Receipting Donation GL code is missing with split receipting on.`,
        ],
        [
            of(ACCOUNTING_UNVERIFIED),
            (n) =>
                `${count(n)} not migrated: Veevart could not read whether accounting is on — an integration access problem, not a museum one.`,
        ],
        [
            summary.held,
            (n) => `${count(n)} not migrated for another reason, and the GL code is also missing.`,
        ],
        [
            summary.conflicts,
            (n) =>
                `${count(n)} ${n === 1 ? 'is' : 'are'} held by a Veevart 2.0 conflict with no GL code: ` +
                'whether one is required is known once the conflict is resolved, so ask the museum anyway.',
        ],
        [
            of(GL_CODE_ABSENT),
            (n) =>
                `${count(n)} ${n === 1 ? 'migrates' : 'migrate'} with no GL code (accounting is off in Salesforce or GL-code enforcement is off for the client).`,
        ],
    ];
    return sentences.flatMap(([n, sentence]) => (n === 0 ? [] : [sentence(n)]));
}

/** "N programs in M clients have a GL code gap." and why, or `null` when there is no gap. */
export function glGapsObservation(rows: readonly GlGapRow[]): Observation | null {
    const summary = glGapsSummary(glGapsOf(rows));
    if (summary.programs === 0) return null;
    const lead =
        `${plural(summary.programs, 'program')} in ${plural(summary.clients, 'client')} ` +
        `${summary.programs === 1 ? 'has' : 'have'} a GL code gap.`;
    const caveat =
        summary.capped > 0
            ? [
                  `${plural(summary.capped, 'client has', 'clients have')} more programs than ` +
                      'the runner kept; the counts cover only the ones it kept.',
              ]
            : [];
    return {
        label: 'GL code',
        code: null,
        text: [lead, ...gapSentences(summary), ...caveat].join(' '),
    };
}

// ── CSV: the per-client list CSM works from ─────────────────────────────────

const CSV_HEADER = [
    'Tenant',
    'Slug',
    'Tenant ID',
    'Org ID',
    'Sandbox',
    'Outcome',
    'Program',
    'Program ID',
    'Guest',
    'GL code',
    'Reason code',
    'What to do',
    'Message',
    'Records capped',
    'Analysed at',
] as const;

// The client's reason can be anything: Veevart reads it first.
const NOT_MIGRATED_TODO = withOwner({
    owner: 'veevart',
    fix: "check the client's reason; analyse again once fixed.",
});
// The museum is asked for the GL code; the line keeps the other reason's code.
const HELD_TODO = withOwner({
    owner: 'museum',
    fix: "add a GL code to the program in Salesforce (Auctifera__GL_Code__c), and fix the program's own reason (Reason code, Message); then analyse again.",
});
const CONFLICT_TODO = withOwner({
    owner: 'museum',
    fix: 'add a GL code to the program in Salesforce (Auctifera__GL_Code__c) anyway: whether it is required is known once the Veevart 2.0 conflict (Reason code) is resolved. Veevart analyses the client again then.',
});

function gapWhatToDo(gap: GlGap): string {
    if (gap.kind === 'held') return HELD_TODO;
    if (gap.kind === 'conflict') return CONFLICT_TODO;
    return glWhatToDo(gap.code) ?? '';
}

const yesNo = (value: boolean): string => (value ? 'yes' : 'no');

export function glGapsFileName(job: Pick<JobHeader, 'moduleId' | 'reportId'>): string {
    return `${job.moduleId}-gl-gaps-${job.reportId}.csv`;
}

interface CsvLine {
    readonly tenant: GlGapTenant;
    readonly title: string;
    readonly fields: readonly string[];
}

function tenantFields(tenant: GlGapTenant, outcome: TenantOutcome | null): string[] {
    return [
        tenant.displayName,
        rawText(tenant.slug),
        tenant.tenantId,
        rawText(tenant.orgId),
        yesNo(tenant.isSandbox),
        outcome === null ? '' : OUTCOME_LABEL[outcome],
    ];
}

/**
 * One line per gap (a program held for another reason or a conflict keeps its own
 * code, and says the GL code is also missing), plus one per client the analysis did
 * not migrate with no gap line (so CSM sees every client that needs a word), sorted by
 * client then program. `\r\n` between lines; an empty result is the header alone.
 */
export function toGlGapsCsv(job: JobHeader, rows: readonly TenantRow[]): string {
    const analysedAt = rawText(job.finishedAt);
    const gaps = glGapsOf(rows);
    const withGaps = new Set(gaps.map((gap) => gap.tenant.tenantId));

    const gapLines: CsvLine[] = gaps.map((gap) => ({
        tenant: gap.tenant,
        title: gap.title,
        fields: [
            ...tenantFields(gap.tenant, gap.outcome),
            gap.title,
            gap.sourceId,
            yesNo(gap.guest),
            rawText(gap.glCode),
            gap.code,
            gapWhatToDo(gap),
            rawText(gap.message),
            yesNo(gap.capped),
            analysedAt,
        ],
    }));
    const clientLines: CsvLine[] = rows
        .filter((row) => row.outcome === 'not_migrated' && !withGaps.has(row.tenant.tenantId))
        .map((row) => ({
            tenant: row.tenant,
            title: '',
            fields: [
                ...tenantFields(row.tenant, row.outcome),
                '',
                '',
                '',
                '',
                rawText(row.reason),
                NOT_MIGRATED_TODO,
                rawText(row.message),
                yesNo(row.recordsTotal > row.records.length),
                analysedAt,
            ],
        }));

    const lines = [...gapLines, ...clientLines].sort(byTenantThenTitle);
    return [CSV_HEADER, ...lines.map((line) => line.fields)]
        .map((fields) => fields.map(csvField).join(','))
        .join('\r\n');
}
